import React, { useState, useCallback } from 'react';
import Button from 'react-bootstrap/Button';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { tableHandler } from '../../../redux/handlers';
import { useGlobalTable } from '../../../hooks/useGlobalTable';
import { createTableColumns } from '../../../utils/createTableColumns';
import { AllderTable } from '../../../components/AllderTable';
import { CreateZoneModal, ZoneManagementModal, ConfirmDeleteModal, SuccessDeleteModal } from './modals';

const INITIAL_FORM_DATA = {
  id: null,
  name: '',
  prefix: '',
  table_capacity: null,
  seat_capacity: 4,
  is_status: true
};

export function TableManagement() {
  const dispatch = useDispatch();
  const { businessSlug, branchID } = useParams();

  const [formdata, setFormdata] = useState(INITIAL_FORM_DATA);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [modalcreateShow, setModalcreateShow] = useState(false);
  const [modalDeleteShow, setModalDeleteShow] = useState(false);
  const [modalsuccessShow, setModalsuccessShow] = useState(false);
  const [modaleditShow, setModaleditShow] = useState(false);

  const fetchTableZones = useCallback(
    params =>
      dispatch(
        tableHandler.getBranchTableZonePagination(businessSlug, {
          branchId: branchID,
          ...params
        })
      ),
    [businessSlug, branchID, dispatch]
  );

  const {
    data: tableZones,
    loading,
    totalRows,
    paginationModel,
    setPaginationModel,
    handleSort,
    debouncedSearch,
    refreshData: refreshTableZones,
    filterParams
  } = useGlobalTable({
    fetchData: fetchTableZones,
    defaultSort: 'created_at',
    defaultOrder: 'desc',
    defaultPageSize: 20
  });

  const handleExport = async () => {
    try {
      await dispatch(tableHandler.getBranchTableZoneExcel(businessSlug, { s: filterParams.s, branchId: branchID }));
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  const handleCreateZone = async values => {
    try {
      await dispatch(
        tableHandler.createBranchTableZoneAndTable(businessSlug, {
          branch_id: parseInt(branchID, 10),
          name: values.name,
          prefix: values.prefix,
          table_capacity: parseInt(values.table_capacity, 10),
          seat_capacity: parseInt(values.seat_capacity, 10)
        })
      );
      refreshTableZones();
      setSuccessMsg('เพิ่มโซนสำเร็จ');
      setModalcreateShow(false);
      setModalsuccessShow(true);
      setFormdata(INITIAL_FORM_DATA);
    } catch (error) {
      console.error('Error creating zone:', error);
    }
  };

  const handleUpdateZone = async values => {
    try {
      await dispatch(tableHandler.updateBranchTableZone(businessSlug, values.id, values));
      refreshTableZones();
      setSuccessMsg('แก้ไขโซนสำเร็จ');
      setModaleditShow(false);
      setModalsuccessShow(true);
    } catch (error) {
      console.error('Error updating zone:', error);
    }
  };

  const handleDeleteZone = async () => {
    try {
      await dispatch(tableHandler.deleteBranchTableZone(businessSlug, formdata.id));
      refreshTableZones();
      setSuccessMsg('ลบโซนสำเร็จ');
      setModalDeleteShow(false);
      setModalsuccessShow(true);
      setFormdata(INITIAL_FORM_DATA);
    } catch (error) {
      console.error('Error deleting zone:', error);
    }
  };

  const showDeleteConfirmation = () => {
    setModaleditShow(false);
    setModalDeleteShow(true);
  };

  const columns = createTableColumns(
    [
      {
        field: 'id',
        headerName: 'ลำดับที่',
        width: 150,
        sortable: true,
        renderCell: ({ row }) => {
          const index = tableZones.findIndex(item => item.id === row.id);
          const actualIndex = paginationModel.page * paginationModel.pageSize + index + 1;
          return <div className="ps-3">{actualIndex}</div>;
        }
      },
      {
        field: 'name',
        headerName: 'ชื่อโซน',
        width: 200,
        sortable: true,
        flex: 1,
        renderCell: ({ row }) => <div style={{ color: '#2DC76D', fontWeight: 500 }}>{row.name}</div>
      },
      {
        field: 'prefix',
        headerName: 'ตัวย่อโซน',
        width: 150,
        sortable: true
      },
      {
        field: 'table_count',
        headerName: 'จำนวนโต๊ะ',
        width: 150,
        sortable: false,
        center: true,
        renderCell: ({ row }) => <div className="text-center">{row.branchTable ? row.branchTable.length : 0}</div>
      },
      {
        field: 'is_status',
        headerName: 'สถานะ',
        width: 120,
        sortable: true,
        renderCell: ({ row }) => <div className={`category-status status${row.is_status ? 1 : 0}`}></div>
      },
      {
        field: 'actions',
        headerName: 'จัดการ',
        width: 200,
        sortable: false,
        type: 'actions'
      }
    ],
    row => (
      <div className="d-flex gap-2">
        <Button
          type="button"
          className="btn-edit-st-1"
          onClick={() => {
            setSelectedZoneId(row.id);
            setModaleditShow(true);
          }}
        />
        <Button
          type="button"
          className="btn btn-delete-st-3"
          onClick={() => {
            setFormdata({
              ...INITIAL_FORM_DATA,
              id: row.id,
              name: row.name
            });
            setModalDeleteShow(true);
          }}
        />
      </div>
    )
  );

  return (
    <div style={{ padding: '20px 20px 20px 20px' }}>
      <div className="card card-st-2">
        <div className="card-body px-0 py-3">
          <div className="fs-20 fw-bold tc-8 mb-3 px-4">จัดการโซน/โต๊ะ</div>
          <div className="d-flex align-items-center px-4 mb-4">
            <button className="btn btn-export-st-1 me-auto" onClick={handleExport}>
              Export
            </button>
            <div className="me-3">
              <input
                type="text"
                className="form-control form-st-4"
                placeholder="ค้นหา ชื่อโซน"
                onChange={e => debouncedSearch(e.target.value)}
              />
            </div>
            <Button
              type="button"
              className="btn btn btn-st-7"
              onClick={() => {
                setFormdata(INITIAL_FORM_DATA);
                setModalcreateShow(true);
              }}
            >
              เพิ่มโซน/โต๊ะ
            </Button>
          </div>

          <AllderTable
            data={tableZones}
            loading={loading}
            columns={columns}
            totalRows={totalRows}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            handleSort={handleSort}
          />
        </div>
      </div>

      <CreateZoneModal
        show={modalcreateShow}
        onHide={() => setModalcreateShow(false)}
        onSubmit={handleCreateZone}
        formdata={formdata}
      />

      <ZoneManagementModal
        show={modaleditShow}
        onHide={() => setModaleditShow(false)}
        handleDelete={showDeleteConfirmation}
        onSuccess={msg => {
          if (msg) setSuccessMsg(msg);
          refreshTableZones();
          setModalsuccessShow(true);
        }}
        zoneId={selectedZoneId}
      />

      <ConfirmDeleteModal
        show={modalDeleteShow}
        onHide={() => setModalDeleteShow(false)}
        onConfirm={handleDeleteZone}
        formdata={formdata}
      />

      <SuccessDeleteModal show={modalsuccessShow} onHide={() => setModalsuccessShow(false)} msg={successMsg} />
    </div>
  );
}
